const DEFAULT_MAX_DIMENSION = 1440;
const DEFAULT_TARGET_BYTES = 500 * 1024;
const DEFAULT_MAX_BYTES = 800 * 1024;
const DEFAULT_MIN_QUALITY = 0.55;
const DEFAULT_SKIP_BYTES = 350 * 1024;

export type ProcessImageOptions = {
  maxDimension?: number;
  targetBytes?: number;
  maxBytes?: number;
  minQuality?: number;
  skipProcessingBelowBytes?: number;
  /** Save as this type instead of the file's own, e.g. a big PNG photo as JPEG. */
  outputMimeType?: "image/jpeg";
};

export const ORGANIZATION_LOGO_UPLOAD_OPTIONS: ProcessImageOptions = {
  maxDimension: 800,
  targetBytes: 180 * 1024,
  maxBytes: 300 * 1024,
  minQuality: 0.6,
  skipProcessingBelowBytes: 150 * 1024,
};

export const PUBLIC_PAGE_IMAGE_UPLOAD_OPTIONS: ProcessImageOptions = {
  maxDimension: 1280,
  targetBytes: 350 * 1024,
  maxBytes: 600 * 1024,
  minQuality: 0.6,
  skipProcessingBelowBytes: 250 * 1024,
};

export const ORGANIZATION_BANNER_UPLOAD_OPTIONS: ProcessImageOptions = {
  maxDimension: 1920,
  targetBytes: 450 * 1024,
  maxBytes: 700 * 1024,
  minQuality: 0.6,
  skipProcessingBelowBytes: 300 * 1024,
};

/** Same crop rectangle as Profile / onboarding org banner (16:9). */
export const ORGANIZATION_BANNER_ASPECT_RATIO = 16 / 9;

/**
 * Resize/compress org banner payloads the same way for main-org account upload
 * and subcircle edits (when sending a fresh data URL).
 */
export async function preprocessOrgBannerDataUrl(
  base64Image: string
): Promise<{ dataUrl: string; contentType: string }> {
  let contentType = "image/jpeg";
  if (base64Image.startsWith("data:")) {
    const matches = base64Image.match(/data:([^;]+);base64,/);
    if (matches && matches[1]) {
      contentType = matches[1];
    }
  }

  let dataUrl = base64Image;
  if (base64Image.startsWith("data:image/")) {
    try {
      const bannerFile = await dataUrlToFile(base64Image, "org-banner-upload");
      const processedBanner = await processImageForUpload(
        bannerFile,
        ORGANIZATION_BANNER_UPLOAD_OPTIONS
      );
      dataUrl = await fileToDataUrl(processedBanner);
      contentType = processedBanner.type || contentType;
    } catch (error) {
      console.warn(
        "[preprocessOrgBannerDataUrl] Failed to optimize banner, using original.",
        error
      );
    }
  }

  return { dataUrl, contentType };
}

const loadImageFromFile = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Unable to load image: ${file.name}`));
    };

    image.src = objectUrl;
  });

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas export failed"));
        return;
      }

      resolve(blob);
    }, type, quality);
  });

const getOutputFileName = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  const baseName = dotIndex >= 0 ? fileName.slice(0, dotIndex) : fileName;
  return baseName;
};

const getOutputMimeType = (file: File) => {
  if (file.type === "image/png") {
    return "image/png";
  }

  if (file.type === "image/webp") {
    return "image/webp";
  }

  return "image/jpeg";
};

const getOutputExtension = (mimeType: string) => {
  switch (mimeType) {
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    default:
      return "jpg";
  }
};

export const shouldProcessNoticeImage = (file: File) =>
  file.type.startsWith("image/") && file.type !== "image/gif";

export const processImageForUpload = async (
  file: File,
  options: ProcessImageOptions = {}
): Promise<File> => {
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const targetBytes = options.targetBytes ?? DEFAULT_TARGET_BYTES;
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
  const minQuality = options.minQuality ?? DEFAULT_MIN_QUALITY;
  const skipProcessingBelowBytes =
    options.skipProcessingBelowBytes ?? DEFAULT_SKIP_BYTES;

  const image = await loadImageFromFile(file);
  const outputMimeType = options.outputMimeType ?? getOutputMimeType(file);
  const supportsAlpha =
    outputMimeType === "image/png" || outputMimeType === "image/webp";

  if (
    file.size <= skipProcessingBelowBytes &&
    image.width <= maxDimension &&
    image.height <= maxDimension
  ) {
    return file;
  }

  const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
  let outputWidth = Math.max(1, Math.round(image.width * scale));
  let outputHeight = Math.max(1, Math.round(image.height * scale));
  let quality = 0.82;

  let bestBlob: Blob | null = null;

  while (true) {
    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Unable to create canvas context");
    }

    if (!supportsAlpha) {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, outputWidth, outputHeight);
    }
    ctx.drawImage(image, 0, 0, outputWidth, outputHeight);

    const blob = await canvasToBlob(
      canvas,
      outputMimeType,
      outputMimeType === "image/png" ? undefined : quality
    );

    if (!bestBlob || blob.size < bestBlob.size) {
      bestBlob = blob;
    }

    if (blob.size <= targetBytes) {
      bestBlob = blob;
      break;
    }

    if (blob.size <= maxBytes && quality <= 0.72) {
      bestBlob = blob;
      break;
    }

    if (outputMimeType !== "image/png" && quality > minQuality) {
      quality = Math.max(minQuality, quality - 0.08);
      continue;
    }

    const nextWidth = Math.max(1, Math.round(outputWidth * 0.85));
    const nextHeight = Math.max(1, Math.round(outputHeight * 0.85));

    if (
      (nextWidth === outputWidth && nextHeight === outputHeight) ||
      (outputWidth <= 720 && outputHeight <= 720)
    ) {
      break;
    }

    outputWidth = nextWidth;
    outputHeight = nextHeight;
  }

  if (!bestBlob) {
    throw new Error("Image processing failed");
  }

  const outputFileName = `${getOutputFileName(file.name)}.${getOutputExtension(
    outputMimeType
  )}`;

  if (
    bestBlob.size >= file.size &&
    file.type === outputMimeType &&
    image.width <= maxDimension &&
    image.height <= maxDimension
  ) {
    return file;
  }

  return new File([bestBlob], outputFileName, {
    type: outputMimeType,
    lastModified: Date.now(),
  });
};

export const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const dataUrlToFile = async (
  dataUrl: string,
  fileName: string
): Promise<File> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], fileName, {
    type: blob.type || "image/jpeg",
    lastModified: Date.now(),
  });
};
